/**
 * mutationGate.js — shared default for the session-data mutation gate.
 *
 * Every session-data-mutating playground surface (thread posts, chat sends, graph mutations, envelope
 * edits, New Object) must be gated on BOTH an editable revision (canEditRevision) AND an open session
 * (isSessionOpen) — see PlaygroundShell.canMutateSession in playground/index.js, which publishes the
 * combined predicate on shellInterface.
 *
 * Before this extraction, callers disagreed on how to fall back when a shellInterface (a partial test
 * double, or an older/partial embedding host) doesn't expose the full seam:
 *   - the four widgets (thread, chat, graph, envelopeControls) hand-rolled
 *     `si.canEditRevision() && (!si.isSessionOpen || si.isSessionOpen())` — fail-OPEN when isSessionOpen
 *     is missing.
 *   - details/playground.js called `!!si.canMutateSession?.()` — fail-CLOSED when canMutateSession
 *     itself is missing.
 * Two different defaults for one seam is a trap for the next caller. This helper is now the single
 * source of truth: prefer the combined predicate when the shellInterface exposes it (the real
 * PlaygroundShell always does, and so does every fixture that cares about the distinction), and
 * otherwise fail-open on isSessionOpen only — the convention the widgets already established (and the
 * one their existing test fixtures assume, so unifying here avoids rewriting them).
 *
 * Pure: no state, no React.
 */
export function canMutateSession(shellInterface) {
  const si = shellInterface;
  if ( !si ) return false;
  if ( si.canMutateSession ) return !!si.canMutateSession();
  if ( !si.canEditRevision || !si.canEditRevision() ) return false;
  return !si.isSessionOpen || si.isSessionOpen();
}
